import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PlayEventSeries } from '@court-sync/shared';
import {
  canExtendPlaySeriesSchedule,
  formatPlaySeriesSchedulePeriod,
  isPlaySeriesEndApproaching,
  isPlaySeriesScheduleEnded,
  parsePlaySeriesScheduleEndsAt,
} from '@court-sync/shared';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PlayEventSeriesDrawer } from '@/components/play/PlayEventSeriesDrawer';
import { PlayEventSeriesSessionsPanel } from '@/components/play/PlayEventSeriesSessionsPanel';
import { DeletePlayEventSeriesConfirmDialog } from '@/components/play/DeletePlayEventSeriesConfirmDialog';
import { CancelPlayEventSeriesConfirmDialog } from '@/components/play/CancelPlayEventSeriesConfirmDialog';
import { DeactivatePlayEventSeriesConfirmDialog } from '@/components/play/DeactivatePlayEventSeriesConfirmDialog';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import {
  DashboardDataCard,
  dashboardTableCellClass,
  dashboardTableDataRowClass,
  dashboardTableHeadClass,
  dashboardTableHeaderRowClass,
} from '@/components/dashboard/DashboardSectionCard';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useCurrentClub } from '@/hooks/useCurrentClub';
import { playEventService } from '@/services/playEvent';
import {
  PLAY_EVENT_FORMAT_LABELS,
  PLAY_EVENT_SIDE_LABELS,
} from '@/lib/playEventLabels';
import {
  AlertTriangle,
  CalendarClock,
  ChevronRight,
  List,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Repeat,
  Trash2,
  XCircle,
} from 'lucide-react';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface PlayEventSeriesSectionHandle {
  openCreate: () => void;
}

export const PlayEventSeriesSection = forwardRef<PlayEventSeriesSectionHandle>(function PlayEventSeriesSection(
  _props,
  ref
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const seriesDrillId = searchParams.get('seriesId') ?? undefined;
  const { currentClub } = useCurrentClub();
  const [series, setSeries] = useState<PlayEventSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSeries, setEditingSeries] = useState<PlayEventSeries | null>(null);
  const [seriesToDelete, setSeriesToDelete] = useState<PlayEventSeries | null>(null);
  const [seriesToCancel, setSeriesToCancel] = useState<PlayEventSeries | null>(null);
  const [seriesToDeactivate, setSeriesToDeactivate] = useState<PlayEventSeries | null>(null);
  const [seriesActionLoading, setSeriesActionLoading] = useState(false);

  const loadSeries = useCallback(async () => {
    if (!currentClub) return;
    setLoading(true);
    try {
      const res = await playEventService.listSeries(currentClub.organizationId);
      if (res.success && res.data) {
        setSeries(res.data);
      } else {
        setSeries([]);
        if (res.error) toast.error(res.error);
      }
    } finally {
      setLoading(false);
    }
  }, [currentClub]);

  useEffect(() => {
    void loadSeries();
  }, [loadSeries]);

  const openCreate = () => {
    setEditingSeries(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: PlayEventSeries) => {
    setEditingSeries(row);
    setDrawerOpen(true);
  };

  const viewSeriesSessions = (row: PlayEventSeries) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'series');
    next.set('seriesId', row.id);
    setSearchParams(next);
  };

  const clearSeriesDrillDown = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('seriesId');
    next.set('tab', 'series');
    setSearchParams(next, { replace: true });
  };

  const drilledSeries = seriesDrillId
    ? series.find((row) => row.id === seriesDrillId) ?? null
    : null;

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingSeries(null);
  };

  const handleActivate = async (row: PlayEventSeries) => {
    if (!currentClub) return;

    const res = await playEventService.updateSeries(currentClub.organizationId, row.id, {
      isActive: true,
    });
    if (res.success) {
      toast.success('Series activated');
      await loadSeries();
    } else {
      toast.error(res.error ?? 'Failed to update series');
    }
  };

  const handleToggleActive = (row: PlayEventSeries) => {
    if (!currentClub) return;
    if (row.isActive) {
      setSeriesToDeactivate(row);
      return;
    }
    void handleActivate(row);
  };

  const confirmDeactivateSeries = async () => {
    if (!currentClub || !seriesToDeactivate) return;
    setSeriesActionLoading(true);
    try {
      const res = await playEventService.updateSeries(
        currentClub.organizationId,
        seriesToDeactivate.id,
        { isActive: false }
      );
      if (res.success) {
        toast.success('Series deactivated');
        setSeriesToDeactivate(null);
        await loadSeries();
      } else {
        toast.error(res.error ?? 'Failed to update series');
      }
    } finally {
      setSeriesActionLoading(false);
    }
  };

  const confirmDeleteSeries = async () => {
    if (!currentClub || !seriesToDelete) return;
    setSeriesActionLoading(true);
    try {
      const res = await playEventService.deleteSeries(currentClub.organizationId, seriesToDelete.id);
      if (res.success) {
        toast.success('Series deleted');
        setSeriesToDelete(null);
        await loadSeries();
      } else {
        toast.error(res.error ?? 'Failed to delete series');
      }
    } finally {
      setSeriesActionLoading(false);
    }
  };

  const confirmCancelSeries = async () => {
    if (!currentClub || !seriesToCancel) return;
    setSeriesActionLoading(true);
    try {
      const res = await playEventService.cancelSeries(currentClub.organizationId, seriesToCancel.id);
      if (res.success) {
        toast.success('Series cancelled');
        setSeriesToCancel(null);
        await loadSeries();
      } else {
        toast.error(res.error ?? 'Failed to cancel series');
      }
    } finally {
      setSeriesActionLoading(false);
    }
  };

  const handleExtendSchedule = async (row: PlayEventSeries) => {
    if (!currentClub) return;
    const res = await playEventService.extendSeriesSchedule(currentClub.organizationId, row.id);
    if (res.success) {
      const created = res.data?.createdEventIds?.length ?? 0;
      toast.success(
        created > 0
          ? `Schedule extended. ${created} new event${created === 1 ? '' : 's'} created.`
          : `Schedule extended for another ${formatPlaySeriesSchedulePeriod(row.schedulePeriodMonths ?? 12)}.`
      );
      await loadSeries();
    } else {
      toast.error(res.error ?? 'Failed to extend schedule');
    }
  };

  const approachingEndSeries = series.filter(
    (row) =>
      row.isActive &&
      row.scheduleEndsAt != null &&
      isPlaySeriesEndApproaching(row.scheduleEndsAt)
  );

  useImperativeHandle(ref, () => ({ openCreate }), []);

  if (seriesDrillId) {
    if (loading) {
      return (
        <div className="flex min-h-[160px] items-center justify-center" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
        </div>
      );
    }

    if (!drilledSeries) {
      return (
        <DashboardEmptyStateCard>
          <p className="text-sm text-muted-foreground">Series not found.</p>
          <Button type="button" className="mt-4" variant="outline" onClick={clearSeriesDrillDown}>
            Back to all series
          </Button>
        </DashboardEmptyStateCard>
      );
    }

    return (
      <PlayEventSeriesSessionsPanel series={drilledSeries} onBack={clearSeriesDrillDown} />
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (series.length === 0) {
    return (
      <>
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={Repeat} />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
            No recurring series
          </h3>
          <p className="mx-auto mb-6 max-w-md text-gray-500 dark:text-gray-400">
            Set a weekly schedule once. Choose a 3, 6, or 12 month period — published play events
            are created automatically while the series is active.
          </p>
          <Button type="button" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Create series
          </Button>
        </DashboardEmptyStateCard>

        {currentClub ? (
          <PlayEventSeriesDrawer
            organizationId={currentClub.organizationId}
            clubId={currentClub.id}
            timezone={currentClub.timezone}
            isOpen={drawerOpen}
            onClose={closeDrawer}
            onSaved={() => void loadSeries()}
            series={editingSeries}
          />
        ) : null}

        <DeletePlayEventSeriesConfirmDialog
          open={seriesToDelete != null}
          onOpenChange={(open) => {
            if (!seriesActionLoading && !open) setSeriesToDelete(null);
          }}
          seriesName={seriesToDelete?.name ?? ''}
          loading={seriesActionLoading}
          onConfirm={confirmDeleteSeries}
        />
        <CancelPlayEventSeriesConfirmDialog
          open={seriesToCancel != null}
          onOpenChange={(open) => {
            if (!seriesActionLoading && !open) setSeriesToCancel(null);
          }}
          seriesName={seriesToCancel?.name ?? ''}
          loading={seriesActionLoading}
          onConfirm={confirmCancelSeries}
        />
        <DeactivatePlayEventSeriesConfirmDialog
          open={seriesToDeactivate != null}
          onOpenChange={(open) => {
            if (!seriesActionLoading && !open) setSeriesToDeactivate(null);
          }}
          seriesName={seriesToDeactivate?.name ?? ''}
          loading={seriesActionLoading}
          onConfirm={confirmDeactivateSeries}
        />
      </>
    );
  }

  return (
    <>
      <DashboardDataCard
        toolbar={
          <p className="text-sm text-muted-foreground">
            {series.length} series
          </p>
        }
      >
        {approachingEndSeries.length > 0 ? (
          <div
            className="mb-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
            <div>
              <p className="font-medium">
                {approachingEndSeries.length === 1
                  ? 'A series schedule is ending soon'
                  : `${approachingEndSeries.length} series schedules are ending soon`}
              </p>
              <p className="mt-1 text-amber-800 dark:text-amber-200">
                Open a series to extend for another {formatPlaySeriesSchedulePeriod()} or deactivate
                it to let sessions finish without creating more.
              </p>
            </div>
          </div>
        ) : null}
        <Table>
          <TableHeader>
            <TableRow className={dashboardTableHeaderRowClass}>
              <TableHead className={dashboardTableHeadClass}>Name</TableHead>
              <TableHead className={dashboardTableHeadClass}>Format</TableHead>
              <TableHead className={dashboardTableHeadClass}>Schedule</TableHead>
              <TableHead className={dashboardTableHeadClass}>Status</TableHead>
              <TableHead className={dashboardTableHeadClass} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {series.map((row) => (
              <TableRow key={row.id} className={dashboardTableDataRowClass}>
                <TableCell className={dashboardTableCellClass}>
                  <span className="font-medium text-gray-900 dark:text-white">{row.name}</span>
                </TableCell>
                <TableCell className={dashboardTableCellClass}>
                  <span className="text-sm text-muted-foreground">
                    {PLAY_EVENT_FORMAT_LABELS[row.format]} · {PLAY_EVENT_SIDE_LABELS[row.side]}
                  </span>
                </TableCell>
                <TableCell className={dashboardTableCellClass}>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-muted-foreground">
                      {row.weekday != null && row.defaultStartTime
                        ? `${WEEKDAY_LABELS[row.weekday]} ${row.defaultStartTime}`
                        : '—'}
                    </span>
                    {row.scheduleEndsAt ? (
                      <span className="text-xs text-muted-foreground">
                        Through{' '}
                        {format(parsePlaySeriesScheduleEndsAt(row.scheduleEndsAt), 'd MMM yyyy')}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className={dashboardTableCellClass}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={row.isActive ? 'success' : 'secondary'}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    {row.isActive &&
                    row.scheduleEndsAt &&
                    isPlaySeriesEndApproaching(row.scheduleEndsAt) ? (
                      <Badge variant="warning">Ending soon</Badge>
                    ) : null}
                    {row.isActive &&
                    row.scheduleEndsAt &&
                    isPlaySeriesScheduleEnded(row.scheduleEndsAt) ? (
                      <Badge variant="secondary">Schedule ended</Badge>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className={dashboardTableCellClass}>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`View sessions for ${row.name}`}
                      onClick={() => viewSeriesSessions(row)}
                    >
                      <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={`Actions for ${row.name}`}
                        >
                          <MoreVertical className="h-4 w-4" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => viewSeriesSessions(row)}>
                          <List className="h-4 w-4" aria-hidden />
                          View sessions
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => openEdit(row)}>
                          <Pencil className="h-4 w-4" aria-hidden />
                          Edit
                        </DropdownMenuItem>
                        {canExtendPlaySeriesSchedule(row) ? (
                          <DropdownMenuItem onSelect={() => void handleExtendSchedule(row)}>
                            <CalendarClock className="h-4 w-4" aria-hidden />
                            Extend for another{' '}
                            {formatPlaySeriesSchedulePeriod(row.schedulePeriodMonths ?? 12)}
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem onSelect={() => handleToggleActive(row)}>
                          {row.isActive ? (
                            <PowerOff className="h-4 w-4" aria-hidden />
                          ) : (
                            <Power className="h-4 w-4" aria-hidden />
                          )}
                          {row.isActive ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => setSeriesToCancel(row)}
                        >
                          <XCircle className="h-4 w-4" aria-hidden />
                          Cancel series
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => setSeriesToDelete(row)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                          Delete series
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DashboardDataCard>

      {currentClub ? (
        <PlayEventSeriesDrawer
          organizationId={currentClub.organizationId}
          clubId={currentClub.id}
          timezone={currentClub.timezone}
          isOpen={drawerOpen}
          onClose={closeDrawer}
          onSaved={() => void loadSeries()}
          series={editingSeries}
        />
      ) : null}

      <DeletePlayEventSeriesConfirmDialog
        open={seriesToDelete != null}
        onOpenChange={(open) => {
          if (!seriesActionLoading && !open) setSeriesToDelete(null);
        }}
        seriesName={seriesToDelete?.name ?? ''}
        loading={seriesActionLoading}
        onConfirm={confirmDeleteSeries}
      />
      <CancelPlayEventSeriesConfirmDialog
        open={seriesToCancel != null}
        onOpenChange={(open) => {
          if (!seriesActionLoading && !open) setSeriesToCancel(null);
        }}
        seriesName={seriesToCancel?.name ?? ''}
        loading={seriesActionLoading}
        onConfirm={confirmCancelSeries}
      />
      <DeactivatePlayEventSeriesConfirmDialog
        open={seriesToDeactivate != null}
        onOpenChange={(open) => {
          if (!seriesActionLoading && !open) setSeriesToDeactivate(null);
        }}
        seriesName={seriesToDeactivate?.name ?? ''}
        loading={seriesActionLoading}
        onConfirm={confirmDeactivateSeries}
      />
    </>
  );
});
